import { useEffect, useState } from 'react';
import { getMyPrivacy, updateMyPrivacy } from '../api/user/account.api';
import { showToast } from '../utill/utill';
import '../css/PrivacyConsent.css';

/**
 * 기존 회원 약관/수신동의 수집 모달.
 *
 * 배경: 동의 저장 기능 도입 이전에 가입한 회원은 동의 이력이 없다.
 *   GET /user/my/privacy 는 항상 200을 주지만 terms_agreed_at 등이 null 이다.
 *   이들에게 로그인 후 동의를 받는다.
 *
 * 감지: PATCH 는 terms_agreed_at 를 수정할 수 없으므로(스키마에 terms_* 없음)
 *   terms_agreed_at 만으로 판정하면 응답 후에도 계속 null 이라 매번 재노출된다.
 *   따라서 "아무 동의 이력도 없음" = 세 시각(terms/marketing_privacy/marketing_email
 *   의 *_at)이 모두 null 일 때만 표시한다. 제출 시 PATCH 가 marketing_*_agreed_at 를
 *   스탬프하므로 다음 GET 부터는 조건 불성립 → 재노출되지 않는다.
 *
 * 흐름:
 *   1) active=true 이면 GET /user/my/privacy 조회
 *      - 세 *_at 모두 null → 모달 표시
 *      - 하나라도 기록 있음 → 이미 응답한 회원 → 세션 플래그 set, 미표시
 *      - 오류(500/네트워크) → 앱 차단 방지를 위해 미표시 (다음 진입 시 재시도)
 *   2) 동의 제출 → PATCH /user/my/privacy (마케팅 2종 전송)
 *      → marketing_*_agreed_at 스탬프 → 재노출 차단
 *
 * 필수 이용약관은 원 가입 시 이미 동의했으므로 서버에 재기록하지 않는다
 * (PATCH 스키마에 terms_* 없음). 화면에서는 재확인 게이트로만 사용.
 *
 * @param {{ active: boolean }} props active - 온보딩 완료 등 표시 가능한 상태일 때만 true
 */
const RESOLVED_KEY = 'privacy_consent_resolved';

const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d="M2.5 7L5.5 10L11.5 4"
      stroke="#ffffff"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function PrivacyConsentModal({ active }) {
  const [visible, setVisible] = useState(false);
  const [agreeService, setAgreeService] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [agreeBenefit, setAgreeBenefit] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 최초 진입 시 동의 이력 유무로 표시 여부 판정
  useEffect(() => {
    if (!active) return undefined;
    if (sessionStorage.getItem(RESOLVED_KEY) === 'true') return undefined;
    let alive = true;
    getMyPrivacy()
      .then((data) => {
        if (!alive) return;
        // 세 동의 시각이 모두 비어 있어야(= 아무 응답도 없는 기존 회원) 표시
        const noConsentHistory =
          !data?.terms_agreed_at &&
          !data?.marketing_privacy_agreed_at &&
          !data?.marketing_email_agreed_at;
        if (noConsentHistory) {
          setVisible(true);
        } else {
          sessionStorage.setItem(RESOLVED_KEY, 'true');
        }
      })
      .catch(() => {
        // 오류(500/네트워크)는 화면을 막지 않도록 무시 (플래그 set 안 함 → 다음 진입 재시도)
      });
    return () => {
      alive = false;
    };
  }, [active]);

  // 모달 표시 중 배경 스크롤 잠금
  useEffect(() => {
    if (!visible) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [visible]);

  if (!visible) return null;

  const agreeAll = agreeService && agreeMarketing && agreeBenefit;

  const toggleAll = () => {
    const next = !agreeAll;
    setAgreeService(next);
    setAgreeMarketing(next);
    setAgreeBenefit(next);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    if (!agreeService) {
      showToast('필수 약관에 동의해주세요.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      // 선택 마케팅 2종만 저장(거부도 false로 명시). PATCH가 upsert라 row가 생성되어
      // 다음 로그인부터 GET 200 → 모달 재노출 차단.
      await updateMyPrivacy({
        marketing_privacy_agreed: agreeMarketing,
        marketing_email_agreed: agreeBenefit,
      });
      sessionStorage.setItem(RESOLVED_KEY, 'true');
      showToast('동의가 저장되었습니다.', 'success');
      setVisible(false);
    } catch (err) {
      showToast(err?.normalizedMessage || '동의 저장에 실패했습니다.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="onboarding-modal__overlay" role="dialog" aria-modal="true">
      <div className="onboarding-modal privacy-consent">
        <header className="onboarding-modal__head">
          <div className="onboarding-modal__brand" aria-hidden="true">GF</div>
          <h2 className="onboarding-modal__title">약관 동의 안내</h2>
          <p className="onboarding-modal__subtitle">
            서비스 이용을 위해 약관 동의가 필요합니다. 잠시만 확인해주세요.
          </p>
        </header>

        <div className="onboarding-modal__body">
          <ul className="privacy-consent__list">
            <li className="privacy-consent__row privacy-consent__row--all">
              <button
                type="button"
                className={`privacy-consent__check ${agreeAll ? 'privacy-consent__check--active' : ''}`}
                onClick={toggleAll}
                aria-pressed={agreeAll}
                aria-label="전체 동의하기"
              >
                <IconCheck />
              </button>
              <span className="privacy-consent__text privacy-consent__text--all">전체 동의하기</span>
            </li>

            <li className="privacy-consent__row">
              <button
                type="button"
                className={`privacy-consent__check ${agreeService ? 'privacy-consent__check--active' : ''}`}
                onClick={() => setAgreeService((v) => !v)}
                aria-pressed={agreeService}
                aria-label="그로우핏 이용약관 동의 (필수)"
              >
                <IconCheck />
              </button>
              <span className="privacy-consent__tag privacy-consent__tag--required">필수</span>
              <span className="privacy-consent__text">그로우핏 이용약관</span>
              <a
                className="privacy-consent__view"
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
              >
                보기
              </a>
            </li>

            <li className="privacy-consent__row privacy-consent__row--two-line">
              <button
                type="button"
                className={`privacy-consent__check ${agreeMarketing ? 'privacy-consent__check--active' : ''}`}
                onClick={() => setAgreeMarketing((v) => !v)}
                aria-pressed={agreeMarketing}
                aria-label="개인정보 수집 및 동의 (선택)"
              >
                <IconCheck />
              </button>
              <div className="privacy-consent__text-group">
                <div className="privacy-consent__text-row">
                  <span className="privacy-consent__tag">선택</span>
                  <span className="privacy-consent__text">개인정보 수집 및 동의</span>
                </div>
                <span className="privacy-consent__subtext">이벤트 및 광고성 정보 수신 동의</span>
              </div>
              <a
                className="privacy-consent__view"
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
              >
                보기
              </a>
            </li>

            <li className="privacy-consent__row privacy-consent__row--two-line">
              <button
                type="button"
                className={`privacy-consent__check ${agreeBenefit ? 'privacy-consent__check--active' : ''}`}
                onClick={() => setAgreeBenefit((v) => !v)}
                aria-pressed={agreeBenefit}
                aria-label="AI 뉴스 및 업데이트 받아보기 (선택)"
              >
                <IconCheck />
              </button>
              <div className="privacy-consent__text-group">
                <div className="privacy-consent__text-row">
                  <span className="privacy-consent__tag">선택</span>
                  <span className="privacy-consent__text">AI 뉴스 및 업데이트 받아보기</span>
                </div>
                <span className="privacy-consent__subtext">
                  신규 기능·업데이트 소식과 이벤트·할인 등 회원 전용 혜택 정보를 이메일로 받아봅니다.
                </span>
              </div>
            </li>
          </ul>
        </div>

        <footer className="onboarding-modal__foot privacy-consent__foot">
          <button
            type="button"
            className="privacy-consent__submit"
            onClick={handleSubmit}
            disabled={!agreeService || submitting}
          >
            {submitting ? '저장 중...' : '동의하고 시작하기'}
          </button>
        </footer>
      </div>
    </div>
  );
}
